import { reportsApi } from '@/lib/api/reports'
import { useState, useMemo, useEffect } from 'react'
import {
  FileText,
  Download,
  Heart,
  Wallet,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Minus,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  Info,
} from 'lucide-react'
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Badge,
} from '@/components/ui'
import { LoadingSpinner, EmptyState } from '@/components/shared'
import { PeriodSelector } from '@/components/health'
import {
  useFamily,
  useReportSummary,
  useExportReport,
  useExportStatus,
  getPeriodDisplayName,
  type HealthPeriod,
} from '@/hooks'
import type {
  ReportPeriod,
  ExportFormat,
  ExportJob,
  ReportInsight,
  HealthReportSection,
  FinancialReportSection,
  AlertsReportSection,
} from '@/types'

export function ReportsPage() {
  const [exportError, setExportError] = useState<string | null>(null)
  const [period, setPeriod] = useState<ReportPeriod>('30d')
  const [pendingExportJob, setPendingExportJob] = useState<ExportJob | null>(null)
  const { selectedFamilyId } = useFamily()

  const { data: summary, isLoading, error } = useReportSummary(selectedFamilyId, {
    period,
  })

  const { requestExport, isExporting } = useExportReport(selectedFamilyId)

  // Poll for export status when there's an active export
  const { data: exportStatus } = useExportStatus(
    selectedFamilyId,
    pendingExportJob?.id ?? null,
    {
      enabled: !!pendingExportJob && pendingExportJob.status !== 'COMPLETED' && pendingExportJob.status !== 'FAILED',
      refetchInterval: 2000, // Poll every 2 seconds
    }
  )

  // Derive active export from pending job and polled status (no effect needed)
  const activeExport = useMemo(() => {
    return exportStatus ?? pendingExportJob
  }, [exportStatus, pendingExportJob])

  const handleExport = async (format: ExportFormat) => {
    setExportError(null)
    try {
      const job = await requestExport({
        format,
        period,
        sections: ['health', 'financial', 'alerts', 'summary'],
          })
      setPendingExportJob(job)
    } catch {
      setExportError('Could not request the export. Please try again.')
    }
  }

  useEffect(() => {
    setPendingExportJob(null)
    setExportError(null)
  }, [selectedFamilyId])

  const handleDownload = async () => {
    if (!activeExport || !selectedFamilyId) return
    setExportError(null)
    try {
      const blob = await reportsApi.getExportDownload(selectedFamilyId, activeExport.id)
      if (blob.type.includes('application/json')) {
        const { download_url } = JSON.parse(await blob.text())
        if (!download_url || !download_url.startsWith('https://')) throw new Error('Invalid download URL')
        window.location.assign(download_url)
      } else {
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `family-report.${activeExport.format.toLowerCase()}`
        link.click()
        setTimeout(() => URL.revokeObjectURL(url), 1000)
      }
    } catch {
      setExportError('Could not download the report. Please retry the download.')
    }
  }

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <EmptyState
        icon={FileText}
        title="Unable to load reports"
        description="There was an error loading your reports. Please try again."
        action={{
          label: 'Retry',
          onClick: () => window.location.reload(),
        }}
      />
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
          <p className="text-gray-500">
            Family-wide summaries and exports for {getPeriodDisplayName(period).toLowerCase()}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <PeriodSelector value={period} onChange={setPeriod as (p: HealthPeriod) => void} />
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => handleExport('csv')}
              disabled={isExporting || !summary}
            >
              {isExporting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              CSV
            </Button>
            <Button
              onClick={() => handleExport('pdf')}
              disabled={isExporting || !summary}
            >
              {isExporting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              PDF
            </Button>
          </div>
        </div>
      </div>

      {exportError && <p role="alert" className="text-error-700">{exportError}</p>}
      {/* Export Status Banner */}
      {activeExport && (
        <ExportStatusBanner
          job={activeExport}
          onDownload={handleDownload}
          onRetry={() => handleExport(activeExport.format.toLowerCase() as ExportFormat)}
          onDismiss={() => setPendingExportJob(null)}
        />
      )}

      {/* No data state */}
      {!summary && (
        <EmptyState
          icon={FileText}
          title="No report data"
          description="There's no data available for this period. Connect health or financial accounts to see reports."
        />
      )}

      {summary && (
        <>
          {/* Summary Cards */}
          <div className="grid gap-6 lg:grid-cols-3">
            {summary.health && <HealthSummaryCard data={summary.health} />}
            {summary.financial && <FinancialSummaryCard data={summary.financial} />}
            {summary.alerts && <AlertsSummaryCard data={summary.alerts} />}
          </div>

          {/* Insights */}
          {summary.insights && summary.insights.length > 0 && (
            <InsightsSection insights={summary.insights} />
          )}
        </>
      )}
    </div>
  )
}

function ExportStatusBanner({
  job,
  onDownload,
  onRetry,
  onDismiss,
}: {
  job: ExportJob
  onRetry: () => void
  onDownload: () => void
  onDismiss: () => void
}) {
  const getStatusConfig = () => {
    switch (job.status) {
      case 'PENDING':
        return {
          icon: Clock,
          text: 'Export pending...',
          variant: 'secondary' as const,
        }
      case 'PROCESSING':
        return {
          icon: Loader2,
          text: `Generating ${job.format.toUpperCase()} report...`,
          variant: 'secondary' as const,
          spin: true,
        }
      case 'COMPLETED':
        return {
          icon: CheckCircle,
          text: 'Export ready for download',
          variant: 'success' as const,
        }
      case 'FAILED':
        return {
          icon: XCircle,
          text: job.error_message || 'Export failed',
          variant: 'error' as const,
        }
    }
  }

  const config = getStatusConfig()
  const Icon = config.icon

  return (
    <div
      className={`flex items-center justify-between rounded-lg border p-4 ${
        config.variant === 'success'
          ? 'border-success-200 bg-success-50'
          : config.variant === 'error'
            ? 'border-error-200 bg-error-50'
            : 'border-gray-200 bg-gray-50'
      }`}
    >
      <div className="flex items-center gap-3">
        <Icon
          className={`h-5 w-5 ${
            config.variant === 'success'
              ? 'text-success-600'
              : config.variant === 'error'
                ? 'text-error-600'
                : 'text-gray-600'
          } ${config.spin ? 'animate-spin' : ''}`}
        />
        <span
          className={`text-sm font-medium ${
            config.variant === 'success'
              ? 'text-success-700'
              : config.variant === 'error'
                ? 'text-error-700'
                : 'text-gray-700'
          }`}
        >
          {config.text}
        </span>
      </div>
      <div className="flex items-center gap-2">
        {job.status === 'FAILED' && <Button size="sm" onClick={onRetry}>Retry export</Button>}
        {job.status === 'COMPLETED' && (
          <Button size="sm" onClick={onDownload}>
            <Download className="mr-2 h-4 w-4" />
            Download
          </Button>
        )}
        {(job.status === 'COMPLETED' || job.status === 'FAILED') && (
          <Button variant="ghost" size="sm" onClick={onDismiss}>
            Dismiss
          </Button>
        )}
      </div>
    </div>
  )
}

function HealthSummaryCard({
  data,
}: {
  data: HealthReportSection
}) {
  const overallTrend = data?.trends?.overall
  const TrendIcon = overallTrend === 'improving'
    ? TrendingUp
    : overallTrend === 'declining'
      ? TrendingDown
      : Minus

  const trendColor = overallTrend === 'improving'
    ? 'text-success-600'
    : overallTrend === 'declining'
      ? 'text-error-600'
      : 'text-gray-500'

  const hasData = data?.has_data ?? false
  const metrics = data?.metrics ?? {}

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-error-100">
              <Heart className="h-4 w-4 text-error-600" />
            </div>
            <CardTitle className="text-base">Health</CardTitle>
          </div>
          {hasData && overallTrend && (
            <Badge variant={overallTrend === 'improving' ? 'success' : overallTrend === 'declining' ? 'error' : 'secondary'}>
              <TrendIcon className={`mr-1 h-3 w-3 ${trendColor}`} />
              {overallTrend}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <p className="text-sm text-gray-500">No health data for this period</p>
        ) : (
          <div className="space-y-3">
            <div className="text-sm text-gray-500">
              {(data?.total_data_points ?? 0).toLocaleString()} data points
            </div>
            {metrics.steps && (
              <MetricRow
                label="Daily Steps"
                value={metrics.steps.average.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                change={metrics.steps.change_percent}
              />
            )}
            {metrics.heart_rate && (
              <MetricRow
                label="Avg Heart Rate"
                value={`${metrics.heart_rate.average.toFixed(0)} bpm`}
                change={metrics.heart_rate.change_percent}
              />
            )}
            {metrics.sleep && (
              <MetricRow
                label="Avg Sleep"
                value={`${(metrics.sleep.average / 60).toFixed(1)} hrs`}
                change={metrics.sleep.change_percent}
              />
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function FinancialSummaryCard({
  data,
}: {
  data: FinancialReportSection
}) {
  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)

  const anomalyCount = data?.anomalies?.count ?? 0
  const categories = data?.categories ?? []
  const hasData = data?.has_data ?? false

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-100">
              <Wallet className="h-4 w-4 text-primary-600" />
            </div>
            <CardTitle className="text-base">Financial</CardTitle>
          </div>
          {anomalyCount > 0 && (
            <Badge variant="warning">
              {anomalyCount} anomal{anomalyCount === 1 ? 'y' : 'ies'}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <p className="text-sm text-gray-500">No financial data for this period</p>
        ) : (
          <div className="space-y-3">
            <div className="text-sm text-gray-500">
              {data?.total_transactions ?? 0} transactions
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Total Spending</span>
              <span className="font-medium text-gray-900">{formatCurrency(data?.total_spending ?? 0)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Total Income</span>
              <span className="font-medium text-success-600">{formatCurrency(data?.total_income ?? 0)}</span>
            </div>
            {categories.length > 0 && (
              <div className="border-t pt-3">
                <div className="text-xs font-medium text-gray-500 uppercase mb-2">Top Categories</div>
                {categories.slice(0, 3).map((cat) => (
                  <div key={cat.category} className="flex justify-between text-sm">
                    <span className="text-gray-600 truncate">{cat.category}</span>
                    <span className="text-gray-900">{formatCurrency(cat.amount)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function AlertsSummaryCard({
  data,
}: {
  data: AlertsReportSection
}) {
  const byStatus = data?.by_status ?? { unread: 0, acknowledged: 0, resolved: 0 }
  const bySeverity = data?.by_severity ?? { low: 0, medium: 0, high: 0, critical: 0 }
  const total = data?.total ?? 0

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-warning-100">
              <AlertTriangle className="h-4 w-4 text-warning-600" />
            </div>
            <CardTitle className="text-base">Alerts</CardTitle>
          </div>
          {byStatus.unread > 0 && (
            <Badge variant="error">{byStatus.unread} unread</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {total === 0 ? (
          <p className="text-sm text-gray-500">No alerts for this period</p>
        ) : (
          <div className="space-y-3">
            <div className="text-sm text-gray-500">{total} total alerts</div>

            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-gray-50 p-2">
                <div className="text-lg font-semibold text-gray-900">{byStatus.unread}</div>
                <div className="text-xs text-gray-500">Unread</div>
              </div>
              <div className="rounded-lg bg-gray-50 p-2">
                <div className="text-lg font-semibold text-gray-900">{byStatus.acknowledged}</div>
                <div className="text-xs text-gray-500">Ack'd</div>
              </div>
              <div className="rounded-lg bg-gray-50 p-2">
                <div className="text-lg font-semibold text-gray-900">{byStatus.resolved}</div>
                <div className="text-xs text-gray-500">Resolved</div>
              </div>
            </div>

            {(bySeverity.high > 0 || bySeverity.critical > 0) && (
              <div className="border-t pt-3">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-error-600 font-medium">
                    {bySeverity.high + bySeverity.critical}
                  </span>
                  <span className="text-gray-500">high/critical severity</span>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function MetricRow({
  label,
  value,
  change,
}: {
  label: string
  value: string
  change?: number
}) {
  const isPositive = change !== undefined && change > 0
  const isNegative = change !== undefined && change < 0

  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-gray-600">{label}</span>
      <div className="flex items-center gap-2">
        <span className="font-medium text-gray-900">{value}</span>
        {change !== undefined && change !== 0 && (
          <span
            className={`text-xs ${
              isPositive ? 'text-success-600' : isNegative ? 'text-error-600' : 'text-gray-500'
            }`}
          >
            {isPositive ? '+' : ''}{change.toFixed(1)}%
          </span>
        )}
      </div>
    </div>
  )
}

function InsightsSection({ insights }: { insights: ReportInsight[] }) {
  const getInsightIcon = (type: ReportInsight['type']) => {
    switch (type) {
      case 'health':
        return Heart
      case 'financial':
        return Wallet
      case 'alert':
        return AlertTriangle
      default:
        return Info
    }
  }

  const getSeverityStyles = (severity: ReportInsight['severity']) => {
    switch (severity) {
      case 'critical':
        return 'border-error-200 bg-error-50'
      case 'warning':
        return 'border-warning-200 bg-warning-50'
      default:
        return 'border-gray-200 bg-gray-50'
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Insights</CardTitle>
        <CardDescription>Key observations from this period</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {insights.map((insight) => {
            const Icon = getInsightIcon(insight.type)
            return (
              <div
                key={insight.id}
                className={`flex gap-3 rounded-lg border p-3 ${getSeverityStyles(insight.severity)}`}
              >
                <Icon
                  className={`h-5 w-5 flex-shrink-0 ${
                    insight.severity === 'critical'
                      ? 'text-error-600'
                      : insight.severity === 'warning'
                        ? 'text-warning-600'
                        : 'text-gray-600'
                  }`}
                />
                <div>
                  <div className="font-medium text-gray-900">{insight.title}</div>
                  <div className="text-sm text-gray-600">{insight.description}</div>
                </div>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}
